import api from './api';

/**
 * Customer + Broker/Forwarder option lists for the Pro Detail "Addl Info"
 * typeaheads. Sourced from /cms-lookups/customers and /cms-lookups/brokers as
 * {value, label, description}[] (value = recno, label = name, description =
 * address). Session-cached in module scope (same pattern as scacs.service);
 * the lists are small and effectively static within a session.
 */

export interface LookupOption {
  value: string;
  label: string;
  description?: string;
  /** Muted "#recno" shown after the label in TypeAhead dropdowns (display only). */
  labelSuffix?: string;
}

/** Stamps each option with a muted "#recno" label suffix (value = recno for
 *  these entity lookups) so every picker shows the record number. */
const withRecnoSuffix = (opts: LookupOption[]): LookupOption[] =>
  opts.map((o) => ({ ...o, labelSuffix: `#${o.value}` }));

/**
 * Caches a lookup request for the session, with one exception for 403s.
 *
 * A permission denial cannot resolve without an admin granting the endpoint, so
 * clearing the cache and retrying on the next mount just hammers the server and
 * floods the security audit log — one missing grant on /quote-maint/rate-zones
 * produced 461 denials for a single user in a week. Resolve empty instead and
 * keep the result; callers already render these lists as empty on failure.
 *
 * Any other failure (network, 5xx) still calls `reset` so the next call retries.
 */
function cacheUntilForbidden<T>(
  request: Promise<T>,
  reset: () => void,
  emptyValue: T
): Promise<T> {
  return request.catch((err: any) => {
    if (err?.response?.status === 403) return emptyValue;
    reset();
    throw err;
  });
}

let customersCache: Promise<LookupOption[]> | null = null;
let brokersCache: Promise<LookupOption[]> | null = null;
let rateZonesCache: Promise<LookupOption[]> | null = null;

export async function getCustomers(): Promise<LookupOption[]> {
  if (!customersCache) {
    customersCache = cacheUntilForbidden(
      api.get<LookupOption[]>('/cms-lookups/customers').then((r) => withRecnoSuffix(r.data)),
      () => { customersCache = null; },
      []
    );
  }
  return customersCache;
}

export async function getBrokers(): Promise<LookupOption[]> {
  if (!brokersCache) {
    brokersCache = cacheUntilForbidden(
      api.get<LookupOption[]>('/cms-lookups/brokers').then((r) => withRecnoSuffix(r.data)),
      () => { brokersCache = null; },
      []
    );
  }
  return brokersCache;
}

/**
 * Rate Zone options (V1 pickupratezone_data) for the Addl Info "Rate Zone"
 * field (order/template `templatetype`). value = ratezone code, label =
 * displayname, from non-deleted cms.ratezones. Reuses the existing
 * /quote-maint/rate-zones endpoint.
 */
export async function getRateZones(): Promise<LookupOption[]> {
  if (!rateZonesCache) {
    rateZonesCache = cacheUntilForbidden(
      api.get<LookupOption[]>('/quote-maint/rate-zones').then((r) => r.data),
      () => { rateZonesCache = null; },
      []
    );
  }
  return rateZonesCache;
}

let cmsCodesCache: Promise<LookupOption[]> | null = null;
const dcMapCache = new Map<string, Promise<LookupOption[]>>();

/**
 * CMS Code options for the Addl Info "CMS Code" select (order/template
 * `gwimove`). value = label = cmscode, from non-deleted cms.cmscodes.
 */
export async function getCmsCodes(): Promise<LookupOption[]> {
  if (!cmsCodesCache) {
    cmsCodesCache = cacheUntilForbidden(
      api.get<LookupOption[]>('/cms-lookups/cms-codes').then((r) => r.data),
      () => { cmsCodesCache = null; },
      []
    );
  }
  return cmsCodesCache;
}

/**
 * DC options for the Addl Info "DC" select, scoped to the order/template
 * customer code (value = dc_map.number → saved to `dcnumber`, label =
 * 'number - name'). Cached per customercode; an empty result means the customer
 * has no DCs and the field should fall back to free-text entry. Returns [] for
 * an empty customercode.
 */
export async function getDcMap(customercode: string | number | null | undefined): Promise<LookupOption[]> {
  const cc = String(customercode ?? '').trim();
  if (!cc || cc === '0') return [];
  let cached = dcMapCache.get(cc);
  if (!cached) {
    cached = cacheUntilForbidden(
      api.get<LookupOption[]>('/cms-lookups/dc-map', { params: { customercode: cc } })
        .then((r) => r.data),
      () => { dcMapCache.delete(cc); },
      []
    );
    dcMapCache.set(cc, cached);
  }
  return cached;
}

/** An invoice line-item type (cms.invoice_litypes) for the Pro Invoicing "Item"
 *  typeahead. `name` is what's stored in invoice_lineitem.liitem; `glType` feeds
 *  the line's GL Code; `additionalChargeRecnos` lists every quote additional charge
 *  (quote.additionalcharges.recno) billing under this type — the Quote Charge
 *  Mapping junction overlaid on the legacy single-slot `additionalChargeRecno`
 *  (0 = unmapped), which older backends still serve alone. */
export interface InvoiceLiType {
  recno: number;
  name: string;
  displayName: string;
  glType: string;
  glCode: string;
  additionalChargeRecno: number;
  additionalChargeRecnos?: number[];
}

let liTypesCache: Promise<InvoiceLiType[]> | null = null;

/**
 * Invoice line-item types (cms.invoice_litypes, non-deleted) for the Pro Invoicing
 * "Item" typeahead. Session-cached (small, effectively static). Maintained via the
 * Invoice Line Items data-management page.
 */
export async function getInvoiceLiTypes(): Promise<InvoiceLiType[]> {
  if (!liTypesCache) {
    liTypesCache = cacheUntilForbidden(
      api.get<InvoiceLiType[]>('/cms-lookups/invoice-litypes').then((r) => r.data),
      () => { liTypesCache = null; },
      []
    );
  }
  return liTypesCache;
}

let holdReasonsCache: Promise<string[]> | null = null;

/**
 * Invoice hold reasons (cms.invoice_holdreasons, non-deleted) as string[] for the
 * Pro Invoicing "Hold Reason" select. Session-cached (small, effectively static within
 * a session). value = label = reason, stored verbatim in ordersall.invoiceholdreason.
 * Maintained via the Invoice Hold Reasons data-management page.
 */
export async function getInvoiceHoldReasons(): Promise<string[]> {
  if (!holdReasonsCache) {
    holdReasonsCache = cacheUntilForbidden(
      api.get<string[]>('/cms-lookups/invoice-hold-reasons').then((r) => r.data),
      () => { holdReasonsCache = null; },
      []
    );
  }
  return holdReasonsCache;
}

/**
 * Port typeahead search (cms.ports). Server-filtered — min 2 chars, returns []
 * otherwise. Maps the enriched /cms-lookups/port-lookup rows to option shape
 * (value = recno → saved to portoforigin/portofdischarge, label = port name,
 * description = seaport / UN-LOCODE / country). Not cached (query-driven).
 */
export async function searchPorts(q: string): Promise<LookupOption[]> {
  const term = (q || '').trim();
  if (term.length < 2) return [];
  const { data } = await api.get<Array<{
    recno: number; pport: string; pcountry?: string; seaportcode?: string; unlocode?: string;
  }>>('/cms-lookups/port-lookup', { params: { q: term } });
  return (data || []).map((r) => ({
    value: String(r.recno),
    label: r.pport || String(r.recno),
    description: [r.seaportcode, r.unlocode, r.pcountry].filter(Boolean).join(' · '),
  }));
}

/** A contact row shared by the customer/broker info popups (from *_contacts). */
export interface EntityContact {
  type: string;
  name: string;
  phone: string;
  email: string;
}

/**
 * Read-only customer details (V1 viewcustomer parity) for the Addl Info
 * customer info popup. NULLs arrive as ''; contacts may be empty.
 */
export interface CustomerInfo {
  recno: number;
  name: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  salesrep: string;
  market: string;
  additionalinfo: string;
  contacts: EntityContact[];
}

/**
 * Read-only broker/forwarder details for the Addl Info broker info popup.
 * NULLs arrive as ''. `additionalinfo` is always '' (the brokerforwarder table
 * has no such column; kept for payload parity, never rendered).
 */
export interface BrokerInfo {
  recno: number;
  name: string;
  contact: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  fax: string;
  email: string;
  notes: string;
  additionalinfo: string;
}

/** Fetches one customer's info card. Per-call (not cached) — 404 on unknown recno. */
export const getCustomerInfo = (recno: number): Promise<CustomerInfo> =>
  api.get<CustomerInfo>(`/cms-lookups/customer-info/${recno}`).then((r) => r.data);

/** Fetches one broker/forwarder's info card. Per-call (not cached) — 404 on unknown recno. */
export const getBrokerInfo = (recno: number): Promise<BrokerInfo> =>
  api.get<BrokerInfo>(`/cms-lookups/broker-info/${recno}`).then((r) => r.data);

/** Drops the in-memory caches. Mostly useful for tests. */
export function clearOrderLookupsCache(): void {
  customersCache = null;
  brokersCache = null;
  rateZonesCache = null;
  cmsCodesCache = null;
  dcMapCache.clear();
  holdReasonsCache = null;
  liTypesCache = null;
}
